import { ApiClient } from '../utils/api-client'
import { User, OTPResponse, SignUpData, PatientDetailsResponse } from '../types'

const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://api-uat.mytatva.in',
  headers: {
    'api-key': process.env.REACT_APP_API_KEY || 'lChjFRJce3bxmoS3TSQk5w==',
    'Accept': '*/*',
    'Content-Type': 'text/plain'
  }
}

const apiClient = new ApiClient(API_CONFIG)

// Helper function to trigger auth state change events
const triggerAuthChange = () => {
  window.dispatchEvent(new Event('auth-changed'));
};

export const authService = {
  isAuthenticated(): boolean {
    return localStorage.getItem("auth_token") !== null
  },

  getCurrentUser(): User | null {
    const userJson = localStorage.getItem("current_user")
    return userJson ? JSON.parse(userJson) : null
  },

  async fetchPatientDetails(token: string): Promise<PatientDetailsResponse> {
    try {
      const response = await apiClient.post<PatientDetailsResponse>(
        '/api/v8/patient/get_patient_details',
        {},
        {
          encrypted: true,
          requireAuth: true,
          headers: {
            'token': token
          }
        }
      )

      if (response.data?.code === "1") {
        return response.data
      } else {
        throw new Error(response.data?.message || "Failed to fetch patient details")
      }
    } catch (error: any) {
      console.error('Fetch Patient Details Error:', error)
      throw new Error(error.message || "Failed to fetch patient details")
    }
  },

  async fetchUserDataFromPatientDetails(token: string, baseData: any): Promise<User> {
    try {
      // Fetch patient details using token
      const patientDetailsResponse = await this.fetchPatientDetails(token)

      if (patientDetailsResponse.code === "1") {
        const patientDetails = patientDetailsResponse.data

        // Create complete user object from patient details
        const user: User = {
          id: patientDetails.patient_id || `user-${Date.now()}`,
          name: patientDetails.name || "User",
          email: patientDetails.email || undefined,
          mobile: patientDetails.contact_no || baseData.mobile,
          studyId: patientDetails.study_id || `RWE-${Date.now()}`,
          dob: patientDetails.dob || undefined,
          gender: patientDetails.gender || undefined,
          weight: patientDetails.weight || undefined,
          height: patientDetails.height || undefined,
          city: patientDetails.city || undefined,
          state: patientDetails.state || undefined,
          language: patientDetails.language_name || undefined,
          token: token,
          patientID: patientDetails.patient_id,
          contactNo: patientDetails.contact_no,
          paidUser: baseData.paidUser,
          planName: baseData.planName,
          country_code: patientDetails.country_code || baseData.country_code,
          indication: baseData.indication,
          dr_name: baseData.dr_name,
          dr_phone: baseData.dr_phone,
          severity: patientDetails.severity_name || baseData.severity,
          current_plan_name: baseData.current_plan_name,
          patient_support_program_name: baseData.patient_support_program_name,
          patient_plan_rel_id: baseData.patient_plan_rel_id,
          health_coach_feature_list: baseData.health_coach_feature_list,
          planPackageDurationRelId: baseData.planPackageDurationRelId,
          therapyArea: baseData.therapyArea,
          leadId: patientDetails.lead_id || baseData.leadId,
          urlDiseaseParam: baseData.urlDiseaseParam,
          disease: baseData.disease
        }

        localStorage.setItem("auth_token", token)
        localStorage.setItem("current_user", JSON.stringify(user))

        // Trigger auth state change event
        triggerAuthChange();

        return user
      } else {
        throw new Error(patientDetailsResponse.message || "Failed to fetch patient details")
      }
    } catch (error: any) {
      console.error('Fetch User Data From Patient Details Error:', error)
      throw new Error(error.message || "Failed to fetch user data from patient details")
    }
  },

  async sendOTP(mobile: string): Promise<{ message: string }> {
    if (!/^\d{10}$/.test(mobile)) {
      throw new Error("Please enter a valid 10-digit mobile number")
    }

    try {
      const payload = {
        contactNo: parseInt(mobile),
        otpType: "LOGIN",
        meta: {
          userFrom: "PSP",
          source: "GoodFlip",
        }
      }

      const response = await apiClient.post<OTPResponse>(
        '/api/v8/otp/send',
        payload,
        {
          encrypted: true,
          requireAuth: false,
          headers: {
            'userFrom': 'PSP',
          }
        }
      )

      if (response.data?.code == 1) {
        return {
          message: response.data.message || "OTP sent successfully to your mobile number"
        }
      } else {
        throw new Error(response.data.message || "Failed to send OTP")
      }
    } catch (error: any) {
      console.error('Send OTP Error:', error)

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error("Network error - please check your connection")
      }

      if (error.status === 400) {
        throw new Error("Invalid mobile number or request format")
      } else if (error.status === 429) {
        throw new Error("Too many requests. Please try again later")
      } else if (error.status >= 500) {
        throw new Error("Server error. Please try again later")
      }

      throw new Error(error.message || "Failed to send OTP. Please try again")
    }
  },

  async verifyOTP(mobile: string, otp: string): Promise<{ user: User, isNewUser: boolean }> {
    if (!/^\d{10}$/.test(mobile)) {
      throw new Error("Invalid mobile number")
    }

    if (!/^\d{6}$/.test(otp)) {
      throw new Error("Please enter a valid 6-digit OTP")
    }

    try {
      const payload = {
        contactNo: parseInt(mobile),
        otpType: "LOGIN",
        otp: parseInt(otp),
        completeResponse: false,
        userFrom: "PSP",
        patient_meta_info: {
          source: "GoodFlip",
          support_program_type: "Viemax - RWE"
        }
      }

      const response = await apiClient.post<OTPResponse>(
        '/api/v8/otp/verify',
        payload,
        {
          encrypted: true,
          requireAuth: false,
          headers: {
            'userFrom': 'PSP',
          }
        }
      )

      if (response.data?.code == 1 || response.data?.code == "1") {
        const responseData = response.data.data || {}
        const isNewUser = responseData.newUser || false

        if (!isNewUser) {
          // For existing users, use patient details API directly
          if (!responseData.token) {
            throw new Error("Token not found in OTP response")
          }

          const user = await this.fetchUserDataFromPatientDetails(responseData.token, {
            mobile: mobile,
            paidUser: responseData.paidUser,
            planName: responseData.patientSupportProgramName,
            country_code: responseData.countryCode,
            indication: undefined,
            dr_name: responseData.doctorLinkedDetails?.doctorName,
            dr_phone: responseData.doctorLinkedDetails?.doctorId,
            severity: undefined,
            current_plan_name: undefined,
            patient_support_program_name: responseData.patientSupportProgramName,
            patient_plan_rel_id: undefined,
            health_coach_feature_list: undefined,
            planPackageDurationRelId: undefined,
            therapyArea: undefined,
            leadId: responseData.leadId,
            urlDiseaseParam: responseData.urlDiseaseParam,
            disease: responseData.disease
          })
          return { user, isNewUser }
        } else {
          const user: User = {
            id: responseData.patientID || responseData.registrationID || `user-${Date.now()}`,
            name: responseData.name || "User",
            email: responseData.email || undefined,
            mobile: responseData.contactNo || mobile,
            studyId: responseData.registrationID || `RWE-${Date.now()}`,
            dob: responseData.dob || undefined,
            gender: responseData.gender || undefined,
            weight: responseData.weight || undefined,
            height: responseData.height || undefined,
            city: responseData.city || undefined,
            state: responseData.state || undefined,
            patientID: responseData.patientID,
            contactNo: responseData.contactNo,
            country_code: responseData.countryCode,
            token: responseData.token
          }

          // Store the token and user data temporarily for new users
          // localStorage.setItem("auth_token", responseData.token || "")
          // localStorage.setItem("current_user", JSON.stringify(user))

          return { user, isNewUser }
        }
      } else {
        throw new Error(response.data?.message || "Invalid OTP")
      }
    } catch (error: any) {
      console.error('Verify OTP Error:', error)

      if (error.status === 400) {
        throw new Error("Invalid OTP")
      } else if (error.status === 401) {
        throw new Error("Invalid OTP")
      } else if (error.status >= 500) {
        throw new Error("Server error. Please try again later")
      }

      throw new Error(error.message || "Failed to verify OTP. Please try again")
    }
  },

  async verifyOTPForSignup(userData: SignUpData, otp: string): Promise<{ user: User, isNewUser: boolean }> {
    if (!/^\d{10}$/.test(userData.mobile)) {
      throw new Error("Invalid mobile number")
    }

    if (!/^\d{6}$/.test(otp)) {
      throw new Error("Please enter a valid 6-digit OTP")
    }

    try {
      const payload = {
        contactNo: parseInt(userData.mobile),
        otpType: "LOGIN",
        otp: parseInt(otp),
        completeResponse: false,
        name: userData.name || "",
        study_id: userData.studyId || "",
        dob: userData.dob || "",
        height: userData.height || "",
        weight: userData.weight || "",
        height_unit: userData.height ? "cm" : undefined,
        weight_unit: userData.weight ? "kg" : undefined,
        gender: userData.gender || "M",
        city: userData.city || "",
        state: userData.state || "",
        userFrom: "PSP",
        patient_meta_info: {
          source: "GoodFlip",
          support_program_type: "Viemax - RWE"
        }
      }

      const response = await apiClient.post<OTPResponse>(
        '/api/v8/otp/verify',
        payload,
        {
          encrypted: true,
          requireAuth: false,
          headers: {
            'userFrom': 'PSP',
          }
        }
      )

      if (response.data?.code == 1 || response.data?.code == "1") {
        const responseData = response.data.data || {}
        const isNewUser = responseData.newUser || false

        // Use patient details API directly for signup as well
        if (!responseData.token) {
          throw new Error("Token not found in OTP response")
        }

        const user = await this.fetchUserDataFromPatientDetails(responseData.token, {
          mobile: userData.mobile,
          paidUser: responseData.paidUser,
          planName: responseData.patientSupportProgramName,
          country_code: responseData.countryCode,
          indication: undefined,
          dr_name: responseData.doctorLinkedDetails?.doctorName,
          dr_phone: responseData.doctorLinkedDetails?.doctorId,
          severity: undefined,
          current_plan_name: undefined,
          patient_support_program_name: responseData.patientSupportProgramName,
          patient_plan_rel_id: undefined,
          health_coach_feature_list: undefined,
          planPackageDurationRelId: undefined,
          therapyArea: undefined,
          leadId: responseData.leadId,
          urlDiseaseParam: responseData.urlDiseaseParam,
          disease: responseData.disease
        })

        return { user, isNewUser }
      } else {
        throw new Error(response.data?.message || "Invalid OTP")
      }
    } catch (error: any) {
      console.error('Verify OTP For Signup Error:', error)

      if (error.status === 400) {
        throw new Error("Invalid OTP or user data")
      } else if (error.status === 401) {
        throw new Error("Invalid OTP")
      } else if (error.status >= 500) {
        throw new Error("Server error. Please try again later")
      }

      throw new Error(error.message || "Failed to verify OTP. Please try again")
    }
  },

  async signUp(userData: SignUpData, otp: string): Promise<User> {
    const result = await this.verifyOTPForSignup(userData, otp)
    return result.user
  },

  async updateProfileForNewUser(profileData: {
    name: string
    studyId: string
    dob?: string
    gender?: string
    weight?: string
    height?: string
    city?: string
    state?: string
  }, token: string): Promise<User> {
    try {
      const payload = {
        name: profileData.name,
        study_id: profileData.studyId,
        dob: profileData.dob || undefined,
        gender: profileData.gender || undefined,
        weight: profileData.weight || undefined,
        height: profileData.height || undefined,
        height_unit: profileData.height ? "cm" : undefined,
        weight_unit: profileData.weight ? "kg" : undefined,
        city: profileData.city || undefined,
        state: profileData.state || undefined,
        updateHeightWeight: 1
      }

      const response = await apiClient.post(
        '/api/v8/patient/update_profile',
        payload,
        {
          encrypted: true,
          requireAuth: true,
          headers: {
            'token': token
          }
        }
      )

      if (response.data?.code === 1 || response.data?.code === "1") {
        // Get the updated user data
        const user = await this.fetchUserDataFromPatientDetails(token, {})
        if (user) {
          return user
        }

        throw new Error("Current user not found")
      } else {
        throw new Error(response.data?.message || "Failed to update profile")
      }
    } catch (error: any) {
      console.error('Update Profile For New User Error:', error)

      if (error.status === 401) {
        throw new Error("Authentication failed. Please log in again")
      } else if (error.status === 400) {
        throw new Error("Invalid profile data")
      } else if (error.status >= 500) {
        throw new Error("Server error. Please try again later")
      }

      throw new Error(error.message || "Failed to update profile. Please try again")
    }
  },

  async updateProfile(profileData: {
    name: string
    email?: string
    dob?: string
    gender?: string
    weight?: string
    height?: string
    city?: string
    state?: string
  }): Promise<User> {
    try {
      const payload = {
        name: profileData.name,
        email: profileData.email || undefined,
        dob: profileData.dob || undefined,
        gender: profileData.gender || undefined,
        weight: profileData.weight || undefined,
        height: profileData.height || undefined,
        height_unit: profileData.height ? "cm" : undefined,
        weight_unit: profileData.weight ? "kg" : undefined,
        city: profileData.city || undefined,
        state: profileData.state || undefined,
        updateHeightWeight: 1
      }

      const response = await apiClient.post(
        '/api/v8/patient/update_profile',
        payload,
        {
          encrypted: true,
          requireAuth: true
        }
      )

      if (response.data?.code === 1 || response.data?.code === "1") {
        // Update the current user in localStorage with new data
        const currentUser = this.getCurrentUser()
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            name: profileData.name,
            email: profileData.email,
            dob: profileData.dob,
            gender: profileData.gender,
            weight: profileData.weight,
            height: profileData.height,
            city: profileData.city,
            state: profileData.state
          }

          localStorage.setItem("current_user", JSON.stringify(updatedUser))
          return updatedUser
        }

        throw new Error("Current user not found")
      } else {
        throw new Error(response.data?.message || "Failed to update profile")
      }
    } catch (error: any) {
      console.error('Update Profile Error:', error)

      if (error.status === 401) {
        throw new Error("Authentication failed. Please log in again")
      } else if (error.status === 400) {
        throw new Error("Invalid profile data")
      } else if (error.status >= 500) {
        throw new Error("Server error. Please try again later")
      }

      throw new Error(error.message || "Failed to update profile. Please try again")
    }
  },

  signOut(): void {
    localStorage.clear()
    // Trigger auth state change event
    triggerAuthChange();
  }
} 